/* Everything the 3D network view renders that is a function of its data.
 *
 * Pulled out of the component because none of it needs a browser: the legend rows, the two
 * translated notices and the label bundle are decisions about what to say, and the layout override
 * is a decision about what to draw. What stays in the view is what genuinely cannot leave - the
 * theme, which resolves CSS custom properties off a mounted element, and the imperative export
 * and view-pane registration.
 */

import { t, type I18nLocale } from '@ardoq/i18n';
import type { ArdoqId } from '@ardoq/api-types';
import {
  toAccountedMarkCount,
  type NetworkContextTarget,
  type NetworkFolded,
  type Network3DViewLabels,
} from '@ardoq/xyz-charts';
import { getRelationshipsViewLegendContents } from 'tabview/relationshipDiagrams/utils';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import type { Network3DSourceData } from '../sourceData/buildNetwork3DSourceData';
import type { Network3DAppViewSettings } from '../types';
import {
  storeCollapsedGroupChange,
  toggleCollapsedGroup,
} from '../collapsedGroups';
import {
  openNetwork3DContextMenu,
  toMarkComponentId,
} from '../network3dContextMenu';

/**
 * The words the package draws, which it cannot translate itself.
 *
 * `degree` and `members` reuse the keys `toAriaLabel` resolves for the canvas's own summary, so
 * the tooltip and the screen-reader label say the same thing in the same language. `toAriaLabel`
 * lives in the view, because it needs the component and reference counts at render time. If
 * either set of fallbacks is reworded, both have to be.
 */
const toLabels = (i18nLocale: I18nLocale): Network3DViewLabels => ({
  degree: count =>
    t(
      i18nLocale,
      count === 1
        ? 'views:network3d.ariaLabel.references_one'
        : 'views:network3d.ariaLabel.references_other',
      { count },
      count === 1 ? '1 reference' : '{{count}} references'
    ),
  members: count =>
    t(
      i18nLocale,
      count === 1
        ? 'views:network3d.ariaLabel.components_one'
        : 'views:network3d.ariaLabel.components_other',
      { count },
      count === 1 ? '1 component' : '{{count}} components'
    ),
  collapseHint: t(
    i18nLocale,
    'views:network3d.tooltip.collapseHint',
    'Double-click to collapse'
  ),
  expandHint: t(
    i18nLocale,
    'views:network3d.tooltip.expandHint',
    'Double-click to expand'
  ),
  noAdapter: t(
    i18nLocale,
    'views:network3d.noAdapter',
    'This view needs WebGPU, which this browser has no adapter for.'
  ),
  deviceLost: t(
    i18nLocale,
    'views:network3d.deviceLost',
    'The graphics device this view was drawing with was lost. Reload the page to get it back.'
  ),
  markTree: t(
    i18nLocale,
    'views:network3d.markTree.label',
    'Groups and components'
  ),
});

/**
 * The notice about references that fall outside the viewpoint.
 *
 * Key and fallback both chosen on the count, as `copyWsToOrg`'s success alert does. The fallback
 * is what an English reader actually sees, so a single `{{count}}` string is not a plural at all:
 * it read "1 references are not shown, because one of their components is outside".
 */
const toDroppedCopy = (
  i18nLocale: I18nLocale,
  droppedReferenceCount: number
): string =>
  t(
    i18nLocale,
    droppedReferenceCount === 1
      ? 'views:network3d.droppedReferencesNotice_one'
      : 'views:network3d.droppedReferencesNotice_other',
    { count: droppedReferenceCount },
    droppedReferenceCount === 1
      ? '1 reference is not shown, because one of its components is outside this viewpoint.'
      : '{{count}} references are not shown, because one of their components is outside this viewpoint.'
  );

/**
 * The legend's rows, from the types the source data collected off the loaded scope.
 *
 * `showReferencesAsLines` is what makes a reference row a line in its own color and dash rather
 * than a swatch, which is the only form that can be compared against what is drawn. Component
 * swatches on, shapes off: this view draws every component as a sphere, and a legend offering
 * shapes would describe a distinction the picture does not make.
 */
const toLegendRows = (
  legend: Network3DSourceData['legend'],
  i18nLocale: I18nLocale
) =>
  getRelationshipsViewLegendContents({
    componentTypes: legend.componentTypes,
    referenceTypes: legend.referenceTypes,
    selectedComponentTypes: [],
    selectedReferenceTypes: [],
    selectedConditionalFormattingIds: [],
    hasCollapsedNodes: false,
    hasNonComponentNodes: false,
    hasReferenceParents: false,
    isUserDefinedGrouping: false,
    showComponentSwatches: true,
    showComponentShapes: false,
    showReferencesAsLines: true,
    selectableItems: false,
    isNewConditionalFormatting: true,
    /* The rows naming what the formatting rules did. Empty before, so a conditionally formatted
       estate showed colors the legend never mentioned. */
    activeConditionalFormattingForLegend: legend.activeConditionalFormatting,
    i18nLocale,
  });

export type Network3DPresentation = {
  /**
   * The settings the canvas is drawn with, which are the reader's except for one override.
   *
   * Grouping picks the layout, because only one of the five can express a grouping. The other
   * four arrange by what the references imply and have nowhere to put a group: drawn by any of
   * them, a grouped graph is the same cloud with some invisible nodes in it. Overriding is the
   * lesser of the two wrongs - the alternative is a Groups panel that appears to work and changes
   * nothing until the reader also finds the layout control.
   *
   * It does mean the Layout setting reads as something other than what is on screen while groups
   * are on, which is a rough edge rather than a design.
   */
  drawnSettings: Network3DAppViewSettings;
  legendRows: ReturnType<typeof getRelationshipsViewLegendContents>;
  viewLegendLabels: ReturnType<typeof getViewLegendLabels>;
  labels: Network3DViewLabels;
  /** Which component a mark stands for, or `null` for a mark that is not one. */
  toComponentId: (nodeId: string) => ArdoqId | null;
  /** Shown in the live region above the canvas, and carried into the exported file. */
  droppedCopy: string;
  /**
   * The notice the exported file has to carry, since that file is the canvas and nothing else.
   *
   * The notice above it is DOM the export does not copy, so without this the PNG claims a
   * complete graph. Worded once, in `droppedCopy`, so the file and the live region cannot
   * disagree.
   */
  exportCaptions: string[];
  /**
   * How many components the picture accounts for, which is not how many marks it draws.
   *
   * It was `graph.nodes.filter(weight !== 0).length` - marks, on the premise that a weightless
   * node is how a group says it is a region rather than a mark. Collapsing broke that premise: a
   * closed group is a mark, so a Collapse all announced "7 components" for seven groups standing
   * for twenty-eight, and a screen-reader user was told the estate had shrunk.
   *
   * Counted through what each mark stands for, so it is stable under a collapse - which is the
   * honest reading, since the components are still in the viewpoint and one gesture brings them
   * back.
   */
  componentCount: number;
  /** Closed groups, so the accessible name can say that part of the picture is folded. */
  collapsedGroupCount: number;
  /**
   * What is folded behind a closed group's mark, or `null` for anything else.
   *
   * A lookup rather than the map itself, because that is the shape the package asks for: it has
   * a node id and no notion of what a group is, and "is there something folded behind this" is
   * the only question it needs answered.
   */
  foldedBehind: (nodeId: string) => NetworkFolded | null;
  /**
   * What a double-click on a group does, or `undefined` where there is no group to act on.
   *
   * `undefined` rather than a no-op, because the package reads it as whether to offer the
   * gesture: an ungrouped viewpoint gets no "Double-click to collapse" under a tooltip for a
   * double-click that would do nothing.
   */
  onNodeActivate: ((nodeId: string) => void) | undefined;
  /** Opens the standard component or reference menu over whatever a secondary click resolved. */
  onContextMenu: (
    target: NetworkContextTarget,
    at: { clientX: number; clientY: number },
    canvas: Network3DCanvasAnswers
  ) => void;
};

/**
 * What only the mounted canvas can answer about the thing that was clicked.
 *
 * Passed in per click rather than derived here, because none of it is a function of the data or
 * the settings - it depends on what the renderer currently has on screen.
 */
export type Network3DCanvasAnswers = {
  /** Flies the camera into the clicked group, or `null` where there is no region to fly into. */
  zoomToGroup: VoidFunction | null;
};

/** Everything derived from the data and the settings, which is all this module knows about. */
export type Network3DPresentationModel = Network3DSourceData & {
  viewSettings: Network3DAppViewSettings;
} & Network3DPresentation;

export const toNetwork3DPresentation = (
  sourceData: Network3DSourceData,
  viewSettings: Network3DAppViewSettings
): Network3DPresentationModel => {
  const {
    isGrouped,
    hasGroups,
    collapsibleGroupIds,
    foldedGroups,
    legend,
    i18nLocale,
    droppedReferenceCount,
    isViewEmpty,
  } = sourceData;
  const droppedCopy = toDroppedCopy(i18nLocale, droppedReferenceCount);
  const foldedBehind = (nodeId: string): NetworkFolded | null =>
    foldedGroups.get(nodeId) ?? null;

  /* Every node the renderer draws as a sphere. A weightless one is a region and is drawn as
     nothing, so it is not a mark and stands for nothing. */
  const markIndices = sourceData.graph.nodes.flatMap((node, index) =>
    node.weight === 0 ? [] : [index]
  );

  const toComponentId = toMarkComponentId(
    sourceData.graph.nodes,
    sourceData.componentIds
  );

  return {
    ...sourceData,
    viewSettings,
    drawnSettings: isGrouped
      ? { ...viewSettings, layoutKind: 'grouped' as const }
      : viewSettings,
    legendRows: toLegendRows(legend, i18nLocale),
    viewLegendLabels: getViewLegendLabels(i18nLocale),
    labels: toLabels(i18nLocale),
    droppedCopy,
    exportCaptions:
      droppedReferenceCount > 0 && !isViewEmpty ? [droppedCopy] : [],
    componentCount: toAccountedMarkCount(
      markIndices,
      sourceData.graph.nodes,
      foldedBehind
    ),
    collapsedGroupCount: foldedGroups.size,
    foldedBehind,
    /* Exposed as well as closed over by the context menu: the hit-test registry answers in
       component ids, and a grouping can draw one component as several marks, so only this knows
       which component a mark stands for. */
    toComponentId,
    onContextMenu: (target, at, { zoomToGroup }) =>
      openNetwork3DContextMenu({
        target,
        at,
        toComponentId,
        referenceIds: sourceData.referenceIds,
        viewInstanceId: sourceData.viewInstanceId,
        i18nLocale,
        zoomToGroup,
      }),
    onNodeActivate: hasGroups
      ? nodeId => {
          /* Nothing written for a mark that is not a group - see `toggleCollapsedGroup`, which
             answers `null` rather than the same list so that this cannot be a rebuild. */
          storeCollapsedGroupChange(
            toggleCollapsedGroup({
              collapsedGroupIds: viewSettings.collapsedGroupIds,
              collapsibleGroupIds,
              groupId: nodeId,
            })
          );
        }
      : undefined,
  };
};
